<template>
  <div>
    <v-row align="end" class="mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
    </v-row>
    <v-row justify="center" class="py-15" no-gutters v-if="loading">
      <v-col cols="auto">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>
    <template v-else>
      <div class="mt-6">
        <h3
          class="text-center"
          v-if="
            filteredUsers.every(
              (user) =>
                !(selectedShifts.includes(1) && user.shift1.meals.length > 0) &&
                !(selectedShifts.includes(2) && user.shift2.meals.length > 0) &&
                !(selectedShifts.includes(3) && user.shift3.meals.length > 0) &&
                !(selectedShifts.includes(4) && user.shift4.meals.length > 0)
            )
          "
        >
          {{ $t("no_orders") }}
        </h3>
        <v-row
          v-for="shift in shiftDefinitions"
          :key="shift.name"
          v-if="
            selectedShifts.includes(shift.number) &&
            filteredUsers.some((user) => user[shift.name].meals.length > 0)
          "
        >
          <v-col cols="12">
            <h2 class="text-center">
              {{ shift.label }}
              (<v-icon small>mdi-account</v-icon>
              {{ usersForShift(shift.name).length }})
            </h2>
          </v-col>
          <v-col
            cols="12"
            lg="3"
            v-for="user in usersForShift(shift.name)"
            :key="user.id"
          >
            <div
              class="fill-height"
              style="border: 1px solid #ccc; padding: 16px; margin-bottom: 16px"
            >
              <h3 class="text-center">
                <span class="grey--text" v-if="user[shift.name].meals[0]">
                  #{{ user[shift.name].meals[0].order_id }}
                </span>
                {{ toLatin(user.name) }} {{ toLatin(user.lastname) }}
              </h3>
              <div v-for="meal in user[shift.name].meals" :key="meal.id" class="mt-3">
                <strong>{{ meal.quantity }}x </strong>{{ toLatin(meal.name) }}
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </template>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";

export default {
  name: "CompanyReportTabOrders",
  mixins: [hasPrinting, hasExcel, hasReportPrinting],
  data() {
    return {
      report: null,
      loading: false,
      selectedShifts: [1, 2, 3, 4], // Po default sve četiri smene
    };
  },
  created() {
    console.log("CompanyReportTabOrders created");
    this.fetchOrders();
  },
  methods: {
    fetchOrders() {
      console.log("Fetching orders for companies:", this.selectedCompanies, "from", this.dates.from, "to", this.dates.to);
      this.loading = true;
      reportService
        .getCompanyUserList(this.selectedCompanies, {
          date: this.dates.from,
          date2: this.dates.to,
        })
        .then((response) => {
          this.report = response.data;
        })
        .catch((error) => {
          console.error("Error fetching meals:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    usersForShift(shiftName) {
      return this.filteredUsers.filter((user) => user[shiftName].meals.length > 0);
    },
  },
  computed: {
    search() {
      return this.$store.state.report.filters.search;
    },
    shiftKeyMap() {
      return {
        "1-smena": "shift1",
        "2-smena": "shift2",
        "3-smena": "shift3",
        "4-smena": "shift4",
      };
    },
    shiftDefinitions() {
      return [
        { name: "shift1", label: this.$t("shift.one"), number: 1 },
        { name: "shift2", label: this.$t("shift.two"), number: 2 },
        { name: "shift3", label: this.$t("shift.three"), number: 3 },
        { name: "shift4", label: this.$t("shift.four"), number: 4 },
      ];
    },
    users() {
      if (!this.report) {
        return [];
      }

      const userMap = new Map();

      (this.report.meals || [])
        .filter((meal) => meal.total > 0 && meal.id)
        .forEach((meal) => {
          Object.entries(meal.users || {}).forEach(([shiftKey, usersInShift]) => {
            const shiftField = this.shiftKeyMap[shiftKey];
            if (!shiftField) {
              return;
            }

            (usersInShift || []).forEach((user) => {
              let userRecord = userMap.get(user.id);
              if (!userRecord) {
                userRecord = {
                  id: user.id,
                  name: user.name,
                  lastname: user.lastname,
                  shift1: { meals: [] },
                  shift2: { meals: [] },
                  shift3: { meals: [] },
                  shift4: { meals: [] },
                };
                userMap.set(user.id, userRecord);
              }

              const existingMeal = userRecord[shiftField].meals.find(
                (m) => m.id === meal.id
              );
              if (existingMeal) {
                existingMeal.quantity += user.quantity;
              } else {
                userRecord[shiftField].meals.push({
                  id: meal.id,
                  name: meal.name,
                  order_id: meal.order_id,
                  quantity: user.quantity,
                });
              }
            });
          });
        });

      return Array.from(userMap.values());
    },
    filteredUsers() {
      if (!this.search) {
        return this.users;
      }

      const searchTerm = this.translate(this.search.toLowerCase());

      return this.users.filter((user) => {
        const fullName = `${user.name} ${user.lastname}`.toLowerCase();
        return (
          this.translate(fullName).includes(searchTerm) ||
          this.translate(user.name).includes(searchTerm) ||
          this.translate(user.lastname).includes(searchTerm)
        );
      });
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies.map((company) => company.id);
    },
    dates() {
      return this.$store.state.report.filters.dates;
    },
  },
  watch: {
    selectedCompanies() {
      this.fetchOrders();
    },
    "dates.from"(newVal, oldVal) {
      this.fetchOrders();
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const activeShifts = this.shiftDefinitions.filter((shift) =>
          this.selectedShifts.includes(shift.number)
        );

        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("employees")].join(" ") + ".xlsx",
          sheets: [
            {
              name: this.$t("orders"),
              headers: [
                { text: this.$t("name"), value: "name" },
                ...activeShifts.map((shift) => ({
                  value: shift.name,
                  text: shift.label,
                })),
              ],
              items: (() => {
                // For each user, flatten all meals into rows, one meal per row, with name only in first cell (rowspan in Excel)
                const rows = [];
                this.filteredUsers.forEach((user) => {
                  // Collect all meals with shift info
                  const mealRows = [];
                  activeShifts.forEach(({ name: key }) => {
                    (user[key].meals || []).forEach((meal) => {
                      const mealText = `${meal.quantity}x ${this.toLatin(meal.name)}`;
                      const row = {
                        name: [user.name, user.lastname].join(" "),
                      };

                      activeShifts.forEach(({ name: col }) => {
                        row[col] = col === key ? mealText : "";
                      });

                      mealRows.push(row);
                    });
                  });
                  if (mealRows.length === 0) {
                    // User with no meals, add empty row
                    const emptyRow = {
                      name: [user.name, user.lastname].join(" "),
                    };

                    activeShifts.forEach(({ name: col }) => {
                      emptyRow[col] = "";
                    });

                    mealRows.push(emptyRow);
                  }
                  // For Excel vertical merge, only first row gets rowspans, others get empty name
                  mealRows.forEach((row, idx) => {
                    if (idx === 0 && mealRows.length > 1) {
                      row.colspans = [
                        {
                          col: "name",
                          colspan: mealRows.length,
                        },
                      ];
                    } else if (idx > 0) {
                      row.name = "";
                    }
                  });
                  rows.push(...mealRows);
                });
                return rows;
              })(),
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
    isPrinting() {
      if (this.isPrinting) {
        let appendHtml = `<div style='display: flex;flex-wrap: wrap;'>`;
        if (
          this.filteredUsers.every(
            (user) =>
              !(this.selectedShifts.includes(1) && user.shift1.meals.length > 0) &&
              !(this.selectedShifts.includes(2) && user.shift2.meals.length > 0) &&
              !(this.selectedShifts.includes(3) && user.shift3.meals.length > 0) &&
              !(this.selectedShifts.includes(4) && user.shift4.meals.length > 0)
          )
        ) {
          appendHtml += `<p style='width: 100%; text-align: center;'>${this.$t(
            "no_orders"
          )}</p>`;
        }
        this.shiftDefinitions
          .filter((shift) => this.selectedShifts.includes(shift.number))
          .forEach((shift) => {
            const usersWithMeals = this.usersForShift(shift.name);
            if (usersWithMeals.length > 0) {
              appendHtml += `<h3 style='width: 100%; text-align: center;'>${shift.label}</h3>`;
            }
            usersWithMeals.forEach((user) => {
              appendHtml += `<div style='flex: 0 0 50%;box-sizing: border-box;padding: 12px;border: 1px solid #ccc;'>
                <h4 style="text-align:center;margin-top: 0">${this.toLatin(
                  user.name
                )} ${this.toLatin(user.lastname)}</h4>`;
              user[shift.name].meals.forEach((meal) => {
                appendHtml += `<p>${meal.quantity}x ${this.toLatin(meal.name)}</p>`;
              });
              appendHtml += `</div>`;
            });
          });
        appendHtml += `</div>`;
        this.printDocument({
          title: [this.$t("report"), this.$t("orders")].join(" "),
          afterTableHtml: appendHtml,
          subtitle: this.subtitle,
        });
      }
    },
  },
};
</script>
